import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login, demoLogin, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/dashboard/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const tryDemo = async (role) => {
    setDemoLoading(true);
    try {
      const u = await demoLogin(role);
      toast.success(`Signed in as demo ${u.role}`);
      navigate(u.role === "admin" ? "/dashboard/admin" : "/dashboard");
    } catch {
      toast.error("Demo unavailable. Try again.");
    } finally {
      setDemoLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-slate-200">
        <div className="absolute inset-0 sk-grid-bg opacity-40" />
        <div className="absolute inset-0 sk-radial-glow" />
        <div className="relative p-16 flex flex-col justify-between">
          <Logo size="lg" />
          <div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight max-w-md">
              Payments, <span className="text-[#00D533]">guaranteed</span>. Risk, assumed.
            </h2>
            <p className="mt-4 text-slate-500 max-w-md">
              SelleCheck + SelleRisk + SelleMax — card, eCheck, international escrow, and bank-to-bank
              rails powered by Green.money. Approved merchants paid on time, every time.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Logo size="lg" /></div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight" data-testid="login-title">Sign in</h1>
          <p className="mt-2 text-slate-500">Welcome back to SelleCheck.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-600">Email or username</Label>
              <Input
                id="email" type="text" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#FFFFFF] border-slate-200 focus-visible:ring-[#00D533]/50 h-11"
                placeholder="you@example.com or username"
                autoComplete="username"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-600">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#FFFFFF] border-slate-200 focus-visible:ring-[#00D533]/50 h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit" disabled={submitting}
              className="w-full h-11 bg-[#00D533] hover:bg-[#00E636] text-black font-semibold"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in..." : "Sign in"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs uppercase tracking-wider text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <Button
            onClick={handleGoogle}
            variant="ghost"
            className="w-full h-11 border border-slate-300 bg-slate-50 hover:bg-slate-100"
            data-testid="login-google-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5 13 4.5 4 13.5 4 24.5s9 20 20 20 20-9 20-20c0-1.4-.1-2.7-.4-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.5 29 4.5 24 4.5 16.4 4.5 9.8 8.7 6.3 14.7z"/><path fill="#4CAF50" d="M24 44.5c4.9 0 9.3-1.9 12.7-4.9l-5.9-5c-1.9 1.3-4.3 2-6.8 2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 40.2 16.2 44.5 24 44.5z"/><path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l5.9 5c4.1-3.8 6.4-9.4 6.4-16.4 0-1.4-.1-2.7-.4-4z"/></svg>
            Continue with Google
          </Button>

          <div className="mt-6 p-4 rounded-lg bg-[#00D533]/5 border border-[#00D533]/25" data-testid="login-demo-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[#00D533] text-xs uppercase tracking-[0.18em]">Demo access</div>
                <div className="mt-1 text-sm text-slate-600">Skip the form — try a pre-loaded account.</div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                onClick={() => tryDemo("merchant")}
                disabled={demoLoading}
                className="flex-1 h-10 bg-[#00D533] hover:bg-[#00E636] text-black font-semibold"
                data-testid="login-demo-merchant-btn"
              >
                Demo as Merchant
              </Button>
              <Button
                onClick={() => tryDemo("admin")}
                disabled={demoLoading}
                variant="ghost"
                className="flex-1 h-10 border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10"
                data-testid="login-demo-admin-btn"
              >
                Demo as Admin
              </Button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            New to SelleCheck?{" "}
            <Link to="/register" className="text-[#00D533] hover:underline" data-testid="login-register-link">
              Apply for an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
